import React from "react";
import { Route, Redirect } from "wouter";
import { PermissionPage } from "@/components/ui/permission-page";
import { TierPage } from "@/components/ui/tier-page";

// Broker pages
import Dashboard from "@/pages/broker/Dashboard";
import Agency from "@/pages/broker/Agency";
// Personal commissions for producers/agents (moved from Employee Portal)
import AgentCommissions from "@/pages/employee/AgentCommissions";
import Clients from "@/pages/broker/Clients";
import ClientDetail from "@/pages/broker/ClientDetail";
import COBRADetails from "@/pages/broker/COBRADetails";
import HIPAADetails from "@/pages/broker/HIPAADetails";
import Prospects from "@/pages/broker/Prospects";
import ProspectDetail from "@/pages/broker/ProspectDetail";
import Carriers from "@/pages/broker/Carriers";
import Quoting from "@/pages/broker/Quoting";
import { AcceptedQuoteDetails } from "@/pages/broker/quoting/AcceptedQuoteDetails";
import { RFPDetails } from "@/pages/broker/quoting/RFPDetails";
import { RFPProposals } from "@/pages/broker/quoting/RFPProposals";
import Renewals from "@/pages/broker/Renewals";
import TasksPage from "@/pages/broker/TasksPage";
import Marketing from "@/pages/broker/Marketing";
import Directory from "@/pages/broker/Directory";
import Reports from "@/pages/broker/Reports";
import EmployeeDetail from "@/pages/broker/EmployeeDetail";
import TimeOffOverview from "@/pages/employer/timeoff/Overview";
import TimeOffRequests from "@/pages/employer/timeoff/Requests";
import TimeOffPolicies from "@/pages/employer/timeoff/Policies";
import TimeOffBalances from "@/pages/employer/timeoff/Balances";
import TimeOffSettings from "@/pages/employer/timeoff/Settings";
import TimeOffCalendar from "@/pages/employer/timeoff/Calendar";
import TimeOffHolidays from "@/pages/employer/timeoff/Holidays";
import PlanDetail from "@/pages/broker/PlanDetail";
import Settings from "@/pages/broker/Settings";
import ClientMigration from "@/pages/broker/migration/ClientMigration";
import BulkClientImport from "@/pages/broker/migration/BulkClientImport";
import MigrationHub from "@/pages/broker/migration/MigrationHub";
import { ACAModule } from "@/pages/employer/aca";
import EligibilityMilestones from "@/pages/employer/EligibilityMilestones";

export const BrokerDashboard = Dashboard;

// Permission-wrapped page components
// Pages use useParams() hook internally so no need to pass params as props

const ProtectedAgency = () => (
  <PermissionPage
    anyOf={[
      "agency:view",
      "agency:edit",
      "agency:members",
      "agency:roles",
      // Allow users with only agency commissions permissions to access the Agency page
      // (they will only see the Commissions tab inside Agency).
      "agency_commissions:view",
      "agency_commissions:reports",
      "agency_commissions:manage",
    ]}
    pageTitle="Agency Settings"
  >
    <Agency />
  </PermissionPage>
);

const ProtectedClients = () => (
  <PermissionPage permission="clients:view" pageTitle="Clients">
    <Clients />
  </PermissionPage>
);

const ProtectedClientDetail = () => (
  <PermissionPage permission="clients:view" pageTitle="Client Details">
    <ClientDetail />
  </PermissionPage>
);

const ProtectedProspects = () => (
  <PermissionPage permission="prospects:view" pageTitle="Prospects">
    <Prospects />
  </PermissionPage>
);

const ProtectedProspectDetail = () => (
  <PermissionPage permission="prospects:view" pageTitle="Prospect Details">
    <ProspectDetail />
  </PermissionPage>
);

const ProtectedCarriers = () => (
  <PermissionPage permission="carriers:view" pageTitle="Carriers">
    <Carriers />
  </PermissionPage>
);

const ProtectedQuoting = () => (
  <PermissionPage permission="quotes:view" pageTitle="Quoting">
    <TierPage feature="instant_quote" pageTitle="Quoting">
      <Quoting />
    </TierPage>
  </PermissionPage>
);

const ProtectedAcceptedQuoteDetails = () => (
  <PermissionPage permission="quotes:view" pageTitle="Accepted Quote">
    <AcceptedQuoteDetails />
  </PermissionPage>
);

const ProtectedRFPDetails = () => (
  <PermissionPage permission="quotes:view" pageTitle="RFP Details">
    <TierPage feature="advanced_quoting" pageTitle="Advanced Quoting">
      <RFPDetails />
    </TierPage>
  </PermissionPage>
);

const ProtectedRFPProposals = () => (
  <PermissionPage permission="quotes:view" pageTitle="RFP Proposals">
    <TierPage feature="advanced_quoting" pageTitle="Advanced Quoting">
      <RFPProposals />
    </TierPage>
  </PermissionPage>
);

const ProtectedRenewals = () => (
  <PermissionPage permission="clients:view" pageTitle="Renewals">
    <Renewals />
  </PermissionPage>
);

const ProtectedTasks = () => (
  <PermissionPage permission="tasks:view" pageTitle="Tasks">
    <TasksPage />
  </PermissionPage>
);

const ProtectedMarketing = () => (
  <PermissionPage permission="marketing:view" pageTitle="Marketing">
    <Marketing />
  </PermissionPage>
);

const ProtectedDirectory = () => (
  <PermissionPage permission="directory:view" pageTitle="Directory">
    <Directory />
  </PermissionPage>
);

const ProtectedReports = () => (
  <PermissionPage anyOf={["reports:view", "reports:financial"]} pageTitle="Reports">
    <Reports />
  </PermissionPage>
);

const ProtectedEmployeeDetail = () => (
  <PermissionPage permission="employees:view" pageTitle="Employee Details">
    <EmployeeDetail />
  </PermissionPage>
);

const ProtectedCOBRADetails = () => (
  <PermissionPage permission="clients:view" pageTitle="COBRA Details">
    <COBRADetails />
  </PermissionPage>
);

const ProtectedHIPAADetails = () => (
  <PermissionPage permission="clients:view" pageTitle="HIPAA Details">
    <HIPAADetails />
  </PermissionPage>
);

// Agency self-HR (Phase 7): the agency runs time off for its OWN staff via
// the same pages the employer portal uses — the /api/time-off endpoints are
// scope-polymorphic (session resolves to client scope for employers, agency
// scope for brokers/agency members).
const ProtectedAgencyTimeOff = (Component: React.ComponentType) => () => (
  <PermissionPage anyOf={["agency_hr:view", "agency_hr:manage"]} pageTitle="Agency HR">
    <Component />
  </PermissionPage>
);
const AgencyTimeOffOverview = ProtectedAgencyTimeOff(TimeOffOverview);
const AgencyTimeOffRequests = ProtectedAgencyTimeOff(TimeOffRequests);
const AgencyTimeOffPolicies = ProtectedAgencyTimeOff(TimeOffPolicies);
const AgencyTimeOffBalances = ProtectedAgencyTimeOff(TimeOffBalances);
const AgencyTimeOffSettings = ProtectedAgencyTimeOff(TimeOffSettings);
const AgencyTimeOffCalendar = ProtectedAgencyTimeOff(TimeOffCalendar);
const AgencyTimeOffHolidays = ProtectedAgencyTimeOff(TimeOffHolidays);

// My Commissions - personal commission view for producers/agents
const ProtectedMyCommissions = () => (
  <PermissionPage permission="commissions:view" pageTitle="My Commissions">
    <AgentCommissions />
  </PermissionPage>
);

export function BrokerRoutes() {
  return (
    <>
      <Route path="/agency" component={ProtectedAgency} />
      <Route path="/agency/employees/:employeeId" component={ProtectedEmployeeDetail} />
      <Route path="/clients" component={ProtectedClients} />
      <Route path="/clients/:id" component={ProtectedClientDetail} />
      <Route path="/migration/import" component={BulkClientImport} />
      <Route path="/migration/hub" component={MigrationHub} />
      <Route path="/migration/:id" component={ClientMigration} />
      <Route path="/clients/:id/cobra-details" component={ProtectedCOBRADetails} />
      <Route path="/clients/:id/hipaa-details" component={ProtectedHIPAADetails} />
      <Route path="/prospects" component={ProtectedProspects} />
      <Route path="/prospects/:id" component={ProtectedProspectDetail} />
      <Route path="/prospects/:id/employees/:employeeId" component={ProtectedEmployeeDetail} />
      <Route path="/carriers" component={ProtectedCarriers} />
      <Route path="/renewals" component={ProtectedRenewals} />
      <Route path="/quoting" component={ProtectedQuoting} />
      <Route path="/quoting/accepted/:id" component={ProtectedAcceptedQuoteDetails} />
      <Route path="/quoting/rfp/:id/proposals" component={ProtectedRFPProposals} />
      <Route path="/quoting/rfp/:id" component={ProtectedRFPDetails} />
      <Route path="/tasks" component={ProtectedTasks} />
      <Route path="/tasks/:id">{(params: { id: string }) => <Redirect to={`/tasks?open=${params.id}`} />}</Route>
      <Route path="/tickets">{() => <Redirect to="/tasks" />}</Route>
      <Route path="/marketing" component={ProtectedMarketing} />
      <Route path="/directory" component={ProtectedDirectory} />
      <Route path="/reports" component={ProtectedReports} />
      <Route path="/my-commissions" component={ProtectedMyCommissions} />
      <Route path="/time-off" component={AgencyTimeOffOverview} />
      <Route path="/time-off/requests" component={AgencyTimeOffRequests} />
      <Route path="/time-off/policies" component={AgencyTimeOffPolicies} />
      <Route path="/time-off/balances" component={AgencyTimeOffBalances} />
      <Route path="/time-off/settings" component={AgencyTimeOffSettings} />
      <Route path="/time-off/calendar" component={AgencyTimeOffCalendar} />
      <Route path="/time-off/holidays" component={AgencyTimeOffHolidays} />
      <Route path="/settings" component={Settings} />
      <Route path="/bug-reports">{() => { window.location.href = "/superadmin/bug-reports?role=superadmin"; return null; }}</Route>
      <Route path="/clients/:id/aca">{() => <ACAModule />}</Route>
      <Route path="/prospects/:id/aca">{() => <ACAModule />}</Route>
      <Route path="/eligibility-milestones" component={EligibilityMilestones} />
    </>
  );
}
